import React, { useState } from 'react'
import {
  Clock,
  ArrowRight,
  ShieldCheck,
  Search,
  Navigation,
  Globe2
} from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import Faq from './Faq'
import FooterPage from './FooterPage'

export default function TrackingPage () {
  const [trackingNumber, setTrackingNumber] = useState('')
  const [error, setError] = useState('')
  const [isTracking, setIsTracking] = useState(false)

  const navigate = useNavigate()

  const handleTrackingSubmit = e => {
    e.preventDefault()

    const trimmed = trackingNumber.trim()

    if (!trimmed) {
      setError('Please enter your tracking number.')
      return
    }

    setError('')
    setIsTracking(true)
    navigate(`/track-shipment-v2/${encodeURIComponent(trimmed)}`)
  }

  const features = [
    {
      icon: Navigation,
      title: 'Shipment Visibility',
      description: 'Stay informed about your shipment journey.'
    },
    {
      icon: ShieldCheck,
      title: 'Secure Transportation',
      description: 'Your cargo is handled with care and attention.'
    },
    {
      icon: Clock,
      title: 'Status Updates',
      description: 'Follow shipment progress at every stage.'
    }
  ]

  return (
    <div className='bg-white min-h-screen overflow-hidden'>
      <Helmet>
        <title>Track Your Shipment | S Infinite Express</title>

        <meta
          name='description'
          content='Track your shipments with S INFINITE EXPRESS PVT. LTD. using your LR number. Check shipment progress, delivery status and tracking updates online.'
        />

        <meta
          name='keywords'
          content='S Infinite Express tracking, Track shipment, LR number tracking, courier tracking India, logistics tracking, parcel tracking, cargo tracking, shipment status'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/track-shipment'
        />

        <meta name='author' content='S Infinite Express Pvt. Ltd.' />

        <meta
          property='og:title'
          content='Track Your Shipment | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Track your S Infinite Express shipment using your LR number and stay updated on its delivery journey.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/track-shipment'
        />

        <meta property='og:type' content='website' />

        <meta name='twitter:card' content='summary_large_image' />

        <meta
          name='twitter:title'
          content='Track Shipment | S Infinite Express'
        />

        <meta
          name='twitter:description'
          content='Track your shipment online using your LR number with S Infinite Express.'
        />
      </Helmet>

      {/* HERO SECTION */}
      <section className='relative bg-[#171640] overflow-hidden'>
        {/* Background decoration */}
        <div className='absolute inset-0 opacity-[0.08]'>
          <div
            className='absolute inset-0'
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,.25) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.25) 1px, transparent 1px)',
              backgroundSize: '55px 55px'
            }}
          />
        </div>

        <div className='absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full border-[70px] border-white/[0.04]' />
        <div className='absolute -bottom-52 -left-32 w-[500px] h-[500px] rounded-full border-[70px] border-white/[0.04]' />

        <div className='relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-28 lg:pt-28 lg:pb-36'>
          <div className='grid lg:grid-cols-2 gap-14 lg:gap-16 items-center'>
            {/* LEFT CONTENT */}
            <div>
              <h1 className='text-4xl sm:text-5xl lg:text-[62px] font-black text-white leading-[1.12] mt-7'>
                Your Shipment.
                <span className='block text-[#FFF44F]'>Your Visibility.</span>
              </h1>

              <p className='text-white/70 text-base sm:text-lg leading-8 mt-7 max-w-xl'>
                Every shipment has a journey. Track yours with S Infinite
                Express and stay informed with shipment status updates from
                pickup to delivery.
              </p>

              <div className='flex flex-wrap items-center gap-6 mt-9'>
                <div className='flex items-center gap-2 text-white/80 text-sm'>
                  <ShieldCheck size={19} className='text-[#FFF44F]' />
                  Reliable Logistics
                </div>

                <div className='flex items-center gap-2 text-white/80 text-sm'>
                  <Globe2 size={19} className='text-[#FFF44F]' />
                  Shipment Visibility
                </div>
              </div>
            </div>

            {/* TRACKING CARD */}
            <div className='relative'>
              <div className='absolute -inset-4 bg-[#FFF44F]/10 blur-3xl rounded-full' />

              <div className='relative bg-white rounded-3xl shadow-2xl p-6 sm:p-9'>
                <form onSubmit={handleTrackingSubmit}>
                  <label
                    htmlFor='tracking-number'
                    className='block text-sm font-bold text-[#211f57] mb-3'
                  >
                    Tracking / LR Number
                  </label>

                  <div className='relative'>
                    <Search
                      size={20}
                      className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400'
                    />

                    <input
                      id='tracking-number'
                      type='text'
                      autoComplete='off'
                      placeholder='Enter your LR number'
                      value={trackingNumber}
                      onChange={e => {
                        setTrackingNumber(e.target.value)
                        if (error) setError('')
                      }}
                      className='w-full h-14 pl-12 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-[#211f57] font-semibold placeholder:text-gray-400 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[#211f57]/20 focus:border-[#211f57] transition-all'
                    />
                  </div>

                  {error && (
                    <p className='text-red-600 text-xs font-semibold mt-2'>
                      {error}
                    </p>
                  )}

                  <button
                    type='submit'
                    disabled={isTracking || !trackingNumber.trim()}
                    className='w-full h-14 mt-5 flex items-center justify-center gap-3 rounded-xl bg-[#d32c30] hover:bg-[#b92329] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold transition-all duration-300 shadow-lg shadow-red-900/10'
                  >
                    {isTracking ? (
                      <>
                        <span className='w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin' />
                        Tracking...
                      </>
                    ) : (
                      <>
                        Track Shipment
                        <ArrowRight size={19} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className='absolute bottom-0 left-0 w-full overflow-hidden leading-[0]'>
          <svg
            viewBox='0 0 1440 80'
            className='w-full h-[45px] sm:h-[70px]'
            preserveAspectRatio='none'
          >
            <path
              d='M0,40 C360,100 1080,-20 1440,40 L1440,80 L0,80 Z'
              fill='#ffffff'
            />
          </svg>
        </div>
      </section>

      {/* TRACKING BENEFITS */}
      <section className='py-6 lg:py-8'>
        <div className='max-w-7xl mx-auto px-5 sm:px-8'>
          <div className='text-center max-w-full mx-auto mb-12'>
            <span className='text-[#d32c30] text-xs font-extrabold uppercase tracking-[2px]'>
              Track With Confidence
            </span>

            <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57] mt-4'>
              Stay Connected to Your Shipment
            </h2>

            <p className='text-gray-500 text-sm sm:text-base leading-7 mt-4 sm:whitespace-nowrap'>
              Get better visibility into your shipment journey with a simple and
              convenient tracking experience.
            </p>
          </div>

          <div className='grid md:grid-cols-3 gap-6'>
            {features.map((feature, index) => {
              const Icon = feature.icon

              return (
                <div
                  key={feature.title}
                  className='group p-7 sm:p-8 bg-white border border-gray-100 rounded-2xl hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                >
                  <div className='w-14 h-14 rounded-2xl bg-[#211f57]/5 text-[#211f57] group-hover:bg-[#211f57] group-hover:text-white flex items-center justify-center transition-all'>
                    <Icon size={26} />
                  </div>

                  <h3 className='text-lg font-extrabold text-[#211f57] mt-6'>
                    {feature.title}
                  </h3>

                  <p className='text-sm text-gray-500 leading-7 mt-3'>
                    {feature.description}
                  </p>

                  <div className='w-10 h-1 rounded-full bg-[#d32c30] mt-6 group-hover:w-16 transition-all duration-300' />
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className='py-6 lg:py-8'>
        <div className='max-w-7xl mx-auto px-5 sm:px-8'>
          <div className='relative bg-[#211f57] rounded-3xl overflow-hidden px-4 py-6 sm:px-6 lg:px-8'>
            <div className='absolute -right-20 -top-32 w-80 h-80 rounded-full border-[45px] border-white/[0.05]' />
            <div className='absolute -right-5 -bottom-40 w-72 h-72 rounded-full border-[45px] border-white/[0.05]' />

            <div className='relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8'>
              <div className='max-w-full'>
                <h2 className='text-3xl sm:text-4xl font-extrabold text-white leading-tight'>
                  We're Here to Help
                  <span className='block text-[#FFF44F]'>
                    Every Step of the Way.
                  </span>
                </h2>

                <p className='text-white/65 text-sm sm:text-base leading-7 mt-4 whitespace-nowrap'>
                  Have questions about your shipment? Our team is ready to
                  assist you with your logistics requirements.
                </p>
              </div>

              <a
                href='/contact-us'
                className='shrink-0 inline-flex items-center justify-center gap-3 bg-[#d32c30] hover:bg-white hover:text-[#211f57] text-white px-7 py-4 rounded-xl font-bold text-sm transition-all'
              >
                Contact Our Team
                <ArrowRight size={19} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Faq />
      <FooterPage />
    </div>
  )
}
